import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import {
  filterResults,
  collectFilterOptions,
  computeKpis,
  buildChartData,
  buildQuestionnaireAnalysis,
  buildRecentRows,
  formatPct,
  formatSatisfaction,
  formatDurationSeconds,
} from './dashboardAggregations';

const CHART_COLORS = [
  '#2563eb',
  '#0ea5e9',
  '#16a34a',
  '#f59e0b',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#64748b',
];

function ChartCard({ title, children, empty }) {
  return (
    <div className="dash-chart-card">
      <h3 className="dash-chart-title">{title}</h3>
      {empty ? <p className="dash-chart-empty">Sem dados para exibir</p> : children}
    </div>
  );
}

function truncateLabel(label, max = 28) {
  const s = String(label || '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function ResultadosDashboard({
  results = [],
  questionarios = [],
  onViewResult,
}) {
  const [survey, setSurvey] = useState('Todos');
  const [block, setBlock] = useState('Todos');
  const [local, setLocal] = useState('Todos');

  const filterOptions = useMemo(() => collectFilterOptions(results), [results]);

  const filtered = useMemo(
    () => filterResults(results, { survey, block, local }),
    [results, survey, block, local]
  );

  const kpis = useMemo(
    () => computeKpis(filtered, questionarios),
    [filtered, questionarios]
  );

  const charts = useMemo(() => buildChartData(filtered), [filtered]);

  const analysis = useMemo(
    () => buildQuestionnaireAnalysis(filtered, survey),
    [filtered, survey]
  );

  const recent = useMemo(() => buildRecentRows(filtered, 10), [filtered]);

  const resetFilters = () => {
    setSurvey('Todos');
    setBlock('Todos');
    setLocal('Todos');
  };

  return (
    <section className="resultados-dashboard" aria-label="Dashboard de pesquisas">
      <div className="dash-header">
        <div>
          <h2 className="dash-title">Dashboard</h2>
          <p className="dash-subtitle">Visão geral das pesquisas e respostas</p>
        </div>
      </div>

      <div className="dash-filters">
        <label className="dash-filter">
          <span>Pesquisa</span>
          <select value={survey} onChange={(e) => setSurvey(e.target.value)}>
            <option value="Todos">Todos</option>
            {filterOptions.surveys.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="dash-filter">
          <span>Bloco</span>
          <select value={block} onChange={(e) => setBlock(e.target.value)}>
            <option value="Todos">Todos</option>
            {filterOptions.blocks.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
        <label className="dash-filter">
          <span>Local</span>
          <select value={local} onChange={(e) => setLocal(e.target.value)}>
            <option value="Todos">Todos</option>
            {filterOptions.locals.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="dash-filter-reset" onClick={resetFilters}>
          Limpar filtros
        </button>
      </div>

      <div className="dash-kpi-grid">
        <div className="dash-kpi-card">
          <p className="dash-kpi-label">Total de Pesquisas</p>
          <p className="dash-kpi-value">{kpis.totalPesquisas}</p>
        </div>
        <div className="dash-kpi-card">
          <p className="dash-kpi-label">Total de Respostas</p>
          <p className="dash-kpi-value">{kpis.totalRespostas}</p>
        </div>
        <div className="dash-kpi-card">
          <p className="dash-kpi-label">Pesquisas Ativas</p>
          <p className="dash-kpi-value">{kpis.pesquisasAtivas}</p>
        </div>
        <div className="dash-kpi-card">
          <p className="dash-kpi-label">Taxa com Áudio</p>
          <p className="dash-kpi-value">{formatPct(kpis.pctAudio)}</p>
        </div>
        <div className="dash-kpi-card">
          <p className="dash-kpi-label">Índice de Localização</p>
          <p className="dash-kpi-value">{formatPct(kpis.pctLocal)}</p>
        </div>
        <div className="dash-kpi-card">
          <p className="dash-kpi-label">Índice de Satisfação</p>
          <p className="dash-kpi-value">{formatSatisfaction(kpis.satisfacao)}</p>
          <p className="dash-kpi-hint">Média de avaliações numéricas</p>
        </div>
      </div>

      <div className="dash-charts-grid">
        <ChartCard title="Respostas por Pesquisa" empty={!charts.porPesquisa.length}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.porPesquisa.slice(0, 8)} margin={{ left: 0, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="name"
                tickFormatter={(v) => truncateLabel(v, 14)}
                tick={{ fontSize: 11, fill: '#64748b' }}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip />
              <Bar dataKey="value" name="Respostas" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Respostas por Bloco" empty={!charts.porBloco.length}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.porBloco.slice(0, 8)} layout="vertical" margin={{ left: 8, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis
                type="category"
                dataKey="name"
                width={100}
                tickFormatter={(v) => truncateLabel(v, 16)}
                tick={{ fontSize: 11, fill: '#64748b' }}
              />
              <Tooltip />
              <Bar dataKey="value" name="Respostas" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Respostas por Local" empty={!charts.porLocal.length}>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={charts.porLocal.slice(0, 8)}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) =>
                  `${truncateLabel(name, 12)} ${(percent * 100).toFixed(0)}%`
                }
              >
                {charts.porLocal.slice(0, 8).map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Mídias Anexadas" empty={!charts.midias.length}>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={charts.midias}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                label
              >
                {charts.midias.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Tempo Médio de Áudio">
          <div className="dash-metric-big">
            <span className="dash-metric-big-value">
              {formatDurationSeconds(charts.duracaoMediaSegundos)}
            </span>
            <span className="dash-metric-big-label">média das gravações</span>
          </div>
        </ChartCard>

        <ChartCard
          title="Respostas por Dia (30 dias)"
          empty={!charts.temporal.some((d) => d.value > 0)}
        >
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={charts.temporal} margin={{ left: 0, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ''} />
              <Line
                type="monotone"
                dataKey="value"
                name="Respostas"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="dash-analysis">
        <h3 className="dash-section-title">Análise do Questionário</h3>
        <p className="dash-section-hint">
          {survey !== 'Todos'
            ? `Filtrado pela pesquisa “${survey}”.`
            : 'Visão geral de todas as pesquisas. Selecione uma pesquisa no filtro para detalhar.'}
        </p>

        <div className="dash-analysis-grid">
          <ChartCard
            title="Taxa de Participação"
            empty={!analysis.participation.length}
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={analysis.participation.slice(0, 8).map((p) => ({
                  name: truncateLabel(p.question, 22),
                  fullName: p.question,
                  rate: Number(p.rate.toFixed(1)),
                }))}
                layout="vertical"
                margin={{ left: 8, right: 16 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} unit="%" />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={120}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                />
                <Tooltip
                  formatter={(v) => [`${v}%`, 'Participação']}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
                />
                <Bar dataKey="rate" name="Participação" fill="#16a34a" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Perguntas Mais Respondidas"
            empty={!analysis.topQuestions.length}
          >
            <ul className="dash-top-list">
              {analysis.topQuestions.map((q, i) => (
                <li key={q.question}>
                  <span className="dash-top-rank">{i + 1}</span>
                  <span className="dash-top-q" title={q.question}>
                    {q.question}
                  </span>
                  <span className="dash-top-count">
                    {q.answered}/{q.total}
                  </span>
                </li>
              ))}
            </ul>
          </ChartCard>
        </div>

        <div className="dash-answers-by-question">
          <h4 className="dash-subsection-title">Respostas por Pergunta</h4>
          {!analysis.responsesByQuestion.length ? (
            <p className="dash-chart-empty">Sem respostas agregáveis</p>
          ) : (
            <div className="dash-question-cards">
              {analysis.responsesByQuestion.slice(0, 6).map((q) => (
                <div key={q.question} className="dash-question-card">
                  <div className="dash-question-card-head">
                    <strong title={q.question}>{truncateLabel(q.question, 64)}</strong>
                    <span>Total de respostas: {q.totalAnswers}</span>
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={q.options} margin={{ left: 0, right: 8, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="name"
                        tickFormatter={(v) => truncateLabel(v, 10)}
                        angle={-25}
                        textAnchor="end"
                        interval={0}
                        tick={{ fontSize: 10, fill: '#64748b' }}
                        height={50}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip />
                      <Bar dataKey="value" name="Frequência" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="dash-recent">
        <h3 className="dash-section-title">Respostas Recentes</h3>
        <div className="dash-table-wrap">
          <table className="data-table dash-recent-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Pesquisa</th>
                <th>Local</th>
                <th>Áudio</th>
                <th>Data</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 ? (
                <tr>
                  <td colSpan={6} className="dash-table-empty">
                    Nenhuma resposta encontrada
                  </td>
                </tr>
              ) : (
                recent.map((row) => (
                  <tr key={row.id || `${row.surveyName}-${row.timestamp}`}>
                    <td className="col-code" title={row.id}>
                      {row.id ? truncateLabel(row.id, 18) : '—'}
                    </td>
                    <td>{row.surveyName}</td>
                    <td>{row.location}</td>
                    <td>{row.hasAudio ? 'Sim' : 'Não'}</td>
                    <td>
                      {row.timestamp
                        ? format(new Date(row.timestamp), 'dd/MM/yyyy, HH:mm')
                        : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="dash-btn-view"
                        onClick={() => onViewResult?.(row)}
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default ResultadosDashboard;
